import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Save, RotateCcw } from 'lucide-react';

const API = process.env.REACT_APP_API_URL || '';

const DEF = {
  duration_minutes: 20,
  english_count: 10, maths_count: 10, logical_count: 10, reading_count: 5,
  positive_marks: 1.0, negative_marks: 0.25,
  easy_pct: 40, medium_pct: 40, hard_pct: 20,
  pass_score_pct: 40.0,
  score_labels: {
    excellent: 80, very_good: 70, good: 55, average: 40,
  },
  can_go_back: true, can_mark_review: true,
};

export default function AdminAssessmentConfig() {
  const [cfg,     setCfg]     = useState(null);
  const [form,    setForm]    = useState(DEF);
  const [saving,  setSaving]  = useState(false);
  const [saved,   setSaved]   = useState(false);
  const [error,   setError]   = useState('');

  useEffect(() => {
    axios.get(`${API}/api/admin/assessment/config`)
      .then(r => { setCfg(r.data); setForm({ ...DEF, ...r.data }); })
      .catch(() => setError('Failed to load config.'));
  }, []);

  const sf = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const sl = (k, v) => setForm(f => ({ ...f, score_labels: { ...f.score_labels, [k]: v } }));

  const save = async () => {
    setSaving(true); setError(''); setSaved(false);
    try {
      await axios.post(`${API}/api/admin/assessment/config`, form);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save config.');
    } finally {
      setSaving(false);
    }
  };

  const totalQ = (+form.english_count||0) + (+form.maths_count||0) + (+form.logical_count||0) + (+form.reading_count||0);
  const maxMarks = totalQ * (+form.positive_marks||0);
  const totalPct = (+form.easy_pct||0) + (+form.medium_pct||0) + (+form.hard_pct||0);
  const diffOk   = totalPct === 100;

  return (
    <div className="space-y-5 max-w-[800px]">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Test Configuration</h1>
          <p className="text-sm text-slate-500 mt-0.5">Adjust timer, question counts, marking, and thresholds</p>
        </div>
        <Link to="/portal/admin/assessment" className="text-sm text-[#1e73be] hover:underline">← Dashboard</Link>
      </div>

      {error  && <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{error}</div>}
      {saved  && <div className="p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">✅ Configuration saved successfully.</div>}

      {/* Timer */}
      <Section title="⏱ Timer">
        <NumberField label="Duration (minutes)" value={form.duration_minutes}
          onChange={v => sf('duration_minutes', v)} min={5} max={120} />
      </Section>

      {/* Question counts */}
      <Section title="📋 Questions Per Section">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { k: 'english_count',  label: 'English' },
            { k: 'maths_count',    label: 'Mathematics' },
            { k: 'logical_count',  label: 'Logical' },
            { k: 'reading_count',  label: 'Reading' },
          ].map(({ k, label }) => (
            <NumberField key={k} label={label} value={form[k]}
              onChange={v => sf(k, v)} min={0} max={50} />
          ))}
        </div>
        <div className="flex gap-4 mt-2 text-sm text-slate-600 bg-slate-50 rounded-lg p-3">
          <span>Total Questions: <strong className="text-slate-800">{totalQ}</strong></span>
          <span>Max Marks: <strong className="text-slate-800">{maxMarks.toFixed(1)}</strong></span>
        </div>
      </Section>

      {/* Marking scheme */}
      <Section title="✅ Marking Scheme">
        <div className="grid grid-cols-2 gap-4">
          <FloatField label="Correct Answer (+ve marks)" value={form.positive_marks}
            onChange={v => sf('positive_marks', v)} step="0.25" />
          <FloatField label="Wrong Answer (deduction)" value={form.negative_marks}
            onChange={v => sf('negative_marks', v)} step="0.25" />
        </div>
        <p className="text-xs text-slate-400 mt-2">
          Deduction is stored as a positive number (e.g. 0.25 = −0.25 marks for wrong answer).
          Set to 0 to disable negative marking.
        </p>
      </Section>

      {/* Difficulty mix */}
      <Section title="🎯 Difficulty Distribution (must sum to 100%)">
        <div className="grid grid-cols-3 gap-3">
          {[
            { k: 'easy_pct',   label: 'Easy %',   color: '#16a34a' },
            { k: 'medium_pct', label: 'Medium %',  color: '#ca8a04' },
            { k: 'hard_pct',   label: 'Hard %',    color: '#dc2626' },
          ].map(({ k, label, color }) => (
            <div key={k}>
              <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
              <input type="number" min={0} max={100}
                value={form[k]} onChange={e => sf(k, +e.target.value)}
                className="w-full h-10 px-3 border border-slate-300 rounded-lg text-sm outline-none focus:ring-2"
                style={{ borderColor: color + '55' }} />
            </div>
          ))}
        </div>
        <p className={`text-sm mt-2 font-medium ${diffOk ? 'text-green-600' : 'text-red-600'}`}>
          {diffOk ? '✅ Sum = 100%' : `⚠️ Sum = ${totalPct}% (must be 100%)`}
        </p>
      </Section>

      {/* Score thresholds */}
      <Section title="🏆 Performance Labels (% thresholds)">
        <div className="grid grid-cols-2 gap-3">
          {[
            { k: 'excellent',  label: 'Excellent (≥)',  color: '#16a34a' },
            { k: 'very_good',  label: 'Very Good (≥)',  color: '#1d4ed8' },
            { k: 'good',       label: 'Good (≥)',       color: '#ca8a04' },
            { k: 'average',    label: 'Average (≥)',    color: '#ea580c' },
          ].map(({ k, label, color }) => (
            <div key={k}>
              <label className="block text-xs font-medium mb-1" style={{ color }}>{label}</label>
              <input type="number" min={0} max={100}
                value={form.score_labels?.[k] ?? 0} onChange={e => sl(k, +e.target.value)}
                className="w-full h-10 px-3 border border-slate-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-[#1e73be]/30" />
            </div>
          ))}
        </div>
        <p className="text-xs text-slate-400 mt-2">
          If a score doesn't meet any threshold, it's labelled "Needs Improvement".
        </p>
      </Section>

      {/* Pass score */}
      <Section title="📊 Pass Score">
        <FloatField label="Minimum passing percentage (%)" value={form.pass_score_pct}
          onChange={v => sf('pass_score_pct', v)} step="1" />
      </Section>

      {/* UI options */}
      <Section title="⚙️ Test UI Options">
        <div className="space-y-3">
          <Toggle label="Allow candidates to go back to previous questions"
            checked={form.can_go_back} onChange={v => sf('can_go_back', v)} />
          <Toggle label="Allow candidates to mark questions for review"
            checked={form.can_mark_review} onChange={v => sf('can_mark_review', v)} />
        </div>
      </Section>

      {/* Save */}
      <div className="flex gap-3 pb-6">
        <button onClick={() => { setCfg(prev => prev); setForm({ ...DEF, ...(cfg||{}) }); }}
          className="flex items-center gap-2 px-5 h-11 rounded-xl border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50 transition">
          <RotateCcw className="w-4 h-4" /> Reset
        </button>
        <button onClick={save} disabled={saving || !diffOk}
          className="flex items-center gap-2 px-6 h-11 rounded-xl text-white text-sm font-semibold hover:brightness-110 transition disabled:opacity-50"
          style={{ backgroundColor: '#1e73be' }}>
          <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save Configuration'}
        </button>
      </div>
    </div>
  );
}

const Section = ({ title, children }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-5">
    <h3 className="font-semibold text-slate-800 mb-4">{title}</h3>
    {children}
  </div>
);

const NumberField = ({ label, value, onChange, min, max }) => (
  <div>
    <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
    <input type="number" min={min} max={max} value={value} onChange={e => onChange(+e.target.value)}
      className="w-full h-10 px-3 border border-slate-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-[#1e73be]/30" />
  </div>
);

const FloatField = ({ label, value, onChange, step = '1' }) => (
  <div>
    <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
    <input type="number" step={step} min={0} value={value} onChange={e => onChange(parseFloat(e.target.value) || 0)}
      className="w-full h-10 px-3 border border-slate-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-[#1e73be]/30" />
  </div>
);

const Toggle = ({ label, checked, onChange }) => (
  <label className="flex items-center gap-3 cursor-pointer group">
    <div className={`relative w-11 h-6 rounded-full transition ${checked ? '' : 'bg-slate-200'}`}
      style={checked ? { backgroundColor: '#1e73be' } : {}}>
      <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${checked ? 'translate-x-5' : ''}`} />
      <input type="checkbox" className="sr-only" checked={checked} onChange={e => onChange(e.target.checked)} />
    </div>
    <span className="text-sm text-slate-700">{label}</span>
  </label>
);
